// A project's scheduled runs, specialists and feature flags, in the sections and order the
// team settings page uses (settingsSections.ts).
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

import {
  useProjectLearningAgents,
  useSetProjectLearningAgents,
  useSetProjectSpecialist,
  useSetProjectSwitch,
  type ProjectLearningAgents,
} from "../api/hooks/useAgentPortfolio";
import { SettingSubcard } from "./SettingSubcard";
import { SettingsGroup } from "./SettingsGroup";
import { FEATURE_FLAGS, SECTION_TITLES, type FeatureFlag } from "./settingsSections";
import styles from "./ProjectSettingsSections.module.css";
import { scheduledRunsWarning, specialistWarning } from "./switchCostWarnings";

const Toggle = ({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
}) => (
  <label className={styles.toggle}>
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
    Enabled
  </label>
);

const failure = (what: string, error: Error | null) =>
  error && (
    <p className={styles.error}>
      Could not change {what}: {error.message}
    </p>
  );

export const ProjectSettingsSections = ({ projectId }: { projectId: string }) => {
  const { user } = useKindeAuth();
  const state = useProjectLearningAgents(projectId);
  const setEnabled = useSetProjectLearningAgents();
  const setSpecialist = useSetProjectSpecialist(projectId);
  const data = state.data;
  const enabled = data?.enabled ?? false;
  const alwaysOnNames = (data?.always_on ?? []).map((a) => a.label).join(" and ");
  // Disabled until known: an unchecked-while-loading box invites a click that turns a
  // project OFF.
  const busy = setEnabled.isLoading || state.isLoading;

  return (
    <>
      <SettingsGroup title={SECTION_TITLES.runs}>
        <SettingSubcard
          title="Scheduled runs"
          describe={`${alwaysOnNames || "The agents"} and the specialists enabled below work this project on their daily run. Turned off, no run comes here on its own, but you can still start a conversation with them about it.`}
          warning={
            data && !enabled && data.runs_on_usd !== null
              ? scheduledRunsWarning(data.switch_costs, `~$${data.runs_on_usd}`)
              : null
          }
        >
          {state.isError && (
            <p className={styles.error}>
              Could not load this project&apos;s settings.{" "}
              {state.error instanceof Error ? state.error.message : ""}
            </p>
          )}
          <Toggle
            checked={enabled}
            disabled={busy}
            onChange={(on) =>
              setEnabled.mutate({
                project_id: projectId,
                enabled: on,
                actor_email: user?.email ?? "",
              })
            }
          />
          {failure("the scheduled runs", setEnabled.error)}
        </SettingSubcard>
      </SettingsGroup>

      <SettingsGroup title={SECTION_TITLES.specialists}>
        {(data?.specialists ?? []).map((specialist) => (
          <SettingSubcard
            key={specialist.agent}
            title={specialist.label}
            describe={specialist.describe}
            warning={
              data && specialist.first_pass_usd !== null
                ? specialistWarning(data.switch_costs, `~$${specialist.first_pass_usd}`)
                : null
            }
          >
            <Toggle
              checked={specialist.optional ? specialist.enrolled : true}
              // An always-on specialist has no switch of its own.
              disabled={!specialist.optional || busy || setSpecialist.isLoading}
              onChange={(on) =>
                setSpecialist.mutate({
                  agent: specialist.agent,
                  enrolled: on,
                  actor_email: user?.email ?? "",
                })
              }
            />
            {setSpecialist.variables?.agent === specialist.agent &&
              failure(specialist.label, setSpecialist.error)}
          </SettingSubcard>
        ))}
      </SettingsGroup>

      <SettingsGroup title={SECTION_TITLES.flags}>
        {FEATURE_FLAGS.map((flag) => (
          <FlagSubcard
            key={flag.column}
            flag={flag}
            projectId={projectId}
            data={data}
            disabled={busy}
          />
        ))}
      </SettingsGroup>
    </>
  );
};

const FlagSubcard = ({
  flag,
  projectId,
  data,
  disabled,
}: {
  flag: FeatureFlag;
  projectId: string;
  data: ProjectLearningAgents | undefined;
  disabled: boolean;
}) => {
  const mutation = useSetProjectSwitch(flag.column);
  const on = data?.[flag.column] ?? false;
  return (
    <SettingSubcard
      title={flag.title}
      describe={flag.describe}
      warning={data && !on ? flag.warning(data.switch_costs) : null}
    >
      <Toggle
        checked={on}
        disabled={disabled || mutation.isLoading}
        onChange={(enabled) => mutation.mutate({ project_id: projectId, enabled })}
      />
      {failure(flag.title, mutation.error)}
    </SettingSubcard>
  );
};
